import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import {
  subscribe,
  getESGOverview,
  getSupplierAssessments,
  getRiskMatrix,
  getComplianceStatus,
  getEnvironmentalMetrics,
  getSocialMetrics,
  getGovernanceMetrics,
  getCertifications,
  getInitiatives,
  getStakeholderFeedback,
  getESGScoreBreakdown,
  getHighRiskSuppliers,
  getCriticalRisks,
  getComplianceGaps,
  getESGTrends,
  getESGGrade,
  calculateCarbonFootprint,
  refreshData
} from '../../../services/ethics/supplyChainEthicsService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const SupplyChainEthicsOverview = () => {
  const [ethicsData, setEthicsData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('esg');

  useEffect(() => {
    const unsubscribe = subscribe(setEthicsData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'supply_chain_ethics_overview', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = () => {
    setLoading(true);
    refreshData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'supply_chain_ethics_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading || !ethicsData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '600' }}>
            Loading Supply Chain Ethics Overview...
          </p>
        </div>
      </div>
    );
  }

  const esgOverview = getESGOverview();
  const supplierAssessments = getSupplierAssessments();
  const riskMatrix = getRiskMatrix();
  const complianceStatus = getComplianceStatus();
  const environmentalMetrics = getEnvironmentalMetrics();
  const socialMetrics = getSocialMetrics();
  const governanceMetrics = getGovernanceMetrics();
  const certifications = getCertifications();
  const initiatives = getInitiatives();
  const stakeholderFeedback = getStakeholderFeedback();
  const esgBreakdown = getESGScoreBreakdown();
  const highRiskSuppliers = getHighRiskSuppliers();
  const criticalRisks = getCriticalRisks();
  const complianceGaps = getComplianceGaps();
  const trends = getESGTrends();
  const carbonFootprint = calculateCarbonFootprint();

  const OverviewTab = () => {
    const esgGrade = getESGGrade(esgOverview.overallScore);
    
    return (
      <div style={{ padding: '0' }}>
        {/* ESG Score Dashboard */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '30px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: '900',
              marginBottom: '25px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '28px' }}>🌿</span>
              ESG Performance Dashboard
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              {/* Overall Grade */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '25px',
                border: '2px solid rgba(124, 58, 237, 0.3)',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  fontSize: '72px',
                  fontWeight: '900',
                  color: esgGrade.color,
                  marginBottom: '10px',
                  textShadow: `0 2px 10px ${esgGrade.color}80`
                }}>
                  {esgGrade.grade}
                </div>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '800',
                  color: '#ffffff',
                  marginBottom: '8px'
                }}>
                  {esgOverview.overallScore}/100
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Overall ESG Score
                </div>
              </div>

              {/* Environmental */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '25px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: '#22c55e',
                  marginBottom: '12px',
                  textShadow: '0 2px 8px rgba(34, 197, 94, 0.5)'
                }}>
                  {esgOverview.environmentalScore}
                </div>
                <div style={{
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: `${esgOverview.environmentalScore}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                    borderRadius: '4px',
                    boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
                  }}></div>
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Environmental
                </div>
              </div>

              {/* Social */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '25px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: '#60a5fa',
                  marginBottom: '12px',
                  textShadow: '0 2px 8px rgba(96, 165, 250, 0.5)'
                }}>
                  {esgOverview.socialScore}
                </div>
                <div style={{
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: `${esgOverview.socialScore}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)',
                    borderRadius: '4px',
                    boxShadow: '0 0 10px rgba(96, 165, 250, 0.5)'
                  }}></div>
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Social
                </div>
              </div>

              {/* Governance */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '25px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: '#fbbf24',
                  marginBottom: '12px',
                  textShadow: '0 2px 8px rgba(251, 191, 36, 0.5)'
                }}>
                  {esgOverview.governanceScore}
                </div>
                <div style={{
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: `${esgOverview.governanceScore}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
                    borderRadius: '4px',
                    boxShadow: '0 0 10px rgba(251, 191, 36, 0.5)'
                  }}></div>
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Governance
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          {/* Supplier Overview */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '800',
              marginBottom: '20px',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>🏭</span>
              Supplier Overview
            </h3>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: '900',
                color: '#a855f7',
                textShadow: '0 2px 8px rgba(168, 85, 247, 0.5)',
                marginBottom: '8px'
              }}>
                {esgOverview.totalSuppliers}
              </div>
              <div style={{
                color: '#94a3b8',
                fontSize: '13px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Total Suppliers
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                padding: '15px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#22c55e',
                  marginBottom: '5px'
                }}>
                  {esgOverview.assessedSuppliers}
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  Assessed
                </div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                padding: '15px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#60a5fa',
                  marginBottom: '5px'
                }}>
                  {esgOverview.compliantSuppliers}
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  Compliant
                </div>
              </div>
            </div>
            
            <div style={{
              height: '10px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '5px',
              overflow: 'hidden',
              marginBottom: '10px'
            }}>
              <div style={{
                width: `${(esgOverview.compliantSuppliers / esgOverview.totalSuppliers) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                borderRadius: '5px',
                boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
              }}></div>
            </div>
            <div style={{
              color: '#cbd5e1',
              fontSize: '13px',
              fontWeight: '700',
              textAlign: 'center'
            }}>
              {Math.round((esgOverview.compliantSuppliers / esgOverview.totalSuppliers) * 100)}% Compliance Rate
            </div>
          </div>

          {/* Carbon Footprint */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '800',
              marginBottom: '20px',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>💨</span>
              Carbon Footprint
            </h3>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                fontSize: '38px',
                fontWeight: '900',
                color: '#fbbf24',
                textShadow: '0 2px 8px rgba(251, 191, 36, 0.5)',
                marginBottom: '5px'
              }}>
                {carbonFootprint.total.toLocaleString()}
              </div>
              <div style={{
                color: '#94a3b8',
                fontSize: '13px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                tCO2e Total
              </div>
            </div>
            
            {[
              { label: 'Scope 1', percentage: carbonFootprint.breakdown.scope1.percentage, color: '#ef4444' },
              { label: 'Scope 2', percentage: carbonFootprint.breakdown.scope2.percentage, color: '#fbbf24' },
              { label: 'Scope 3', percentage: carbonFootprint.breakdown.scope3.percentage, color: '#60a5fa' }
            ].map((scope, idx) => (
              <div key={idx} style={{ marginBottom: '12px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '6px'
                }}>
                  <span style={{
                    color: '#cbd5e1',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}>
                    {scope.label}
                  </span>
                  <span style={{
                    color: scope.color,
                    fontSize: '13px',
                    fontWeight: '800'
                  }}>
                    {scope.percentage}%
                  </span>
                </div>
                <div style={{
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${scope.percentage}%`,
                    height: '100%',
                    background: scope.color,
                    borderRadius: '3px',
                    boxShadow: `0 0 8px ${scope.color}80`
                  }}></div>
                </div>
              </div>
            ))}
            
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <span style={{
                background: '#22c55e',
                color: '#ffffff',
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '800',
                boxShadow: '0 2px 10px rgba(34, 197, 94, 0.4)'
              }}>
                {carbonFootprint.reduction}% vs Target
              </span>
            </div>
          </div>

          {/* Critical Risks */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '800',
              marginBottom: '20px',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              Critical Risks
            </h3>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: '900',
                color: '#ef4444',
                textShadow: '0 2px 8px rgba(239, 68, 68, 0.5)',
                marginBottom: '8px'
              }}>
                {criticalRisks.length}
              </div>
              <div style={{
                color: '#94a3b8',
                fontSize: '13px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                High Risk Items
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {criticalRisks.slice(0, 3).map((risk, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  padding: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '10px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: '800',
                        marginBottom: '4px'
                      }}>
                        {risk.risk}
                      </div>
                      <div style={{
                        color: '#94a3b8',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {risk.category}
                      </div>
                    </div>
                    <span style={{
                      background: risk.severity === 'High' ? '#ef4444' : 
                                 risk.severity === 'Medium' ? '#fbbf24' : '#64748b',
                      color: '#ffffff',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '800',
                      whiteSpace: 'nowrap'
                    }}>
                      {risk.riskScore.toFixed(0)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Status */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '800',
              marginBottom: '20px',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              Compliance Status
            </h3>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: '900',
                color: '#60a5fa',
                textShadow: '0 2px 8px rgba(96, 165, 250, 0.5)',
                marginBottom: '8px'
              }}>
                {complianceGaps.length}
              </div>
              <div style={{
                color: '#94a3b8',
                fontSize: '13px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Areas for Improvement
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {complianceStatus.slice(0, 3).map((item, index) => (
                <div key={index}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '6px'
                  }}>
                    <span style={{
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '800'
                    }}>
                      {item.framework.split(' ')[0]}
                    </span>
                    <span style={{
                      background: item.complianceLevel >= 90 ? '#22c55e' : 
                                 item.complianceLevel >= 75 ? '#fbbf24' : '#ef4444',
                      color: '#ffffff',
                      padding: '3px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '800'
                    }}>
                      {item.complianceLevel.toFixed(0)}%
                    </span>
                  </div>
                  <div style={{
                    height: '6px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${item.complianceLevel}%`,
                      height: '100%',
                      background: item.complianceLevel >= 90 ? '#22c55e' : 
                                 item.complianceLevel >= 75 ? '#fbbf24' : '#ef4444',
                      borderRadius: '3px',
                      boxShadow: `0 0 8px ${
                        item.complianceLevel >= 90 ? '#22c55e' : 
                        item.complianceLevel >= 75 ? '#fbbf24' : '#ef4444'
                      }80`
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* High Risk Suppliers */}
        {highRiskSuppliers.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              padding: '30px',
              border: '2px solid rgba(251, 191, 36, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h2 style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '900',
                marginBottom: '25px',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '28px' }}>⚠️</span>
                High Risk Suppliers Requiring Attention
              </h2>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: '0 10px'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>Supplier</th>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>Category</th>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>Location</th>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>ESG Score</th>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>Risk Level</th>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>Key Issues</th>
                      <th style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>Last Assessment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {highRiskSuppliers.map((supplier, index) => (
                      <tr key={index} style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        transition: 'all 0.3s ease'
                      }}>
                        <td style={{
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: '800',
                          padding: '15px 12px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {supplier.name}
                        </td>
                        <td style={{
                          padding: '15px 12px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span style={{
                            background: '#64748b',
                            color: '#ffffff',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '800'
                          }}>
                            {supplier.category}
                          </span>
                        </td>
                        <td style={{
                          color: '#cbd5e1',
                          fontSize: '14px',
                          fontWeight: '600',
                          padding: '15px 12px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {supplier.location}
                        </td>
                        <td style={{
                          padding: '15px 12px',
                          textAlign: 'center',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span style={{
                            background: '#ef4444',
                            color: '#ffffff',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '800',
                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                          }}>
                            {supplier.overallScore}
                          </span>
                        </td>
                        <td style={{
                          padding: '15px 12px',
                          textAlign: 'center',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span style={{
                            background: '#ef4444',
                            color: '#ffffff',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '800',
                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                          }}>
                            {supplier.riskLevel}
                          </span>
                        </td>
                        <td style={{
                          color: '#94a3b8',
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '15px 12px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {supplier.issues.slice(0, 2).map((issue, idx) => (
                            <div key={idx} style={{ marginBottom: '3px' }}>{issue}</div>
                          ))}
                        </td>
                        <td style={{
                          color: '#cbd5e1',
                          fontSize: '13px',
                          fontWeight: '700',
                          padding: '15px 12px',
                          textAlign: 'center',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {new Date(supplier.lastAssessment).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Active Initiatives and Stakeholder Feedback Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          {/* Active Initiatives */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '20px',
              fontWeight: '900',
              marginBottom: '20px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '24px' }}>🚀</span>
              Active ESG Initiatives
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {initiatives.map((initiative, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  padding: '18px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    gap: '12px'
                  }}>
                    <h4 style={{
                      color: '#ffffff',
                      fontSize: '16px',
                      fontWeight: '800',
                      margin: 0,
                      flex: 1
                    }}>
                      {initiative.name}
                    </h4>
                    <span style={{
                      background: initiative.status === 'Completed' ? '#22c55e' : 
                                 initiative.status === 'In Progress' ? '#60a5fa' : '#64748b',
                      color: '#ffffff',
                      padding: '5px 12px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                      boxShadow: initiative.status === 'Completed' ? '0 2px 8px rgba(34, 197, 94, 0.4)' :
                                 initiative.status === 'In Progress' ? '0 2px 8px rgba(96, 165, 250, 0.4)' : 'none'
                    }}>
                      {initiative.status}
                    </span>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '15px', alignItems: 'center' }}>
                    <div>
                      <div style={{
                        height: '10px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '5px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          width: `${initiative.progress}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                          borderRadius: '5px',
                          boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
                        }}></div>
                      </div>
                      <div style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        {initiative.impact}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '28px',
                        fontWeight: '900',
                        color: '#22c55e',
                        lineHeight: '1'
                      }}>
                        {initiative.progress}%
                      </div>
                      <div style={{
                        color: '#94a3b8',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase'
                      }}>
                        Complete
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stakeholder Feedback */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#ffffff',
              fontSize: '20px',
              fontWeight: '900',
              marginBottom: '20px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '24px' }}>👥</span>
              Stakeholder Feedback
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {stakeholderFeedback.map((feedback, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  padding: '18px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <h4 style={{
                      color: '#ffffff',
                      fontSize: '16px',
                      fontWeight: '800',
                      margin: 0
                    }}>
                      {feedback.stakeholder}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        color: '#22c55e',
                        fontSize: '15px',
                        fontWeight: '800'
                      }}>
                        {feedback.satisfaction}%
                      </span>
                      <span style={{
                        background: feedback.priority === 'High' ? '#ef4444' : 
                                   feedback.priority === 'Medium' ? '#fbbf24' : '#22c55e',
                        color: '#ffffff',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: feedback.priority === 'High' ? '0 2px 8px rgba(239, 68, 68, 0.4)' :
                                   feedback.priority === 'Medium' ? '0 2px 8px rgba(251, 191, 36, 0.4)' :
                                   '0 2px 8px rgba(34, 197, 94, 0.4)'
                      }}>
                        {feedback.priority}
                      </span>
                    </div>
                  </div>
                  
                  <p style={{
                    color: '#cbd5e1',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    lineHeight: '1.5'
                  }}>
                    {feedback.keyFeedback}
                  </p>
                  
                  <div>
                    <div style={{
                      color: '#ffffff',
                      fontSize: '13px',
                      fontWeight: '800',
                      marginBottom: '6px'
                    }}>
                      Action Items:
                    </div>
                    {feedback.actionItems.map((item, idx) => (
                      <div key={idx} style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        fontWeight: '600',
                        marginBottom: '3px',
                        paddingLeft: '10px'
                      }}>
                        • {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EnvironmentalTab = () => (
    <div style={{ padding: '0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '25px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
        }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: '900',
            marginBottom: '20px',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '24px' }}>🌿</span>
            Environmental Performance
          </h3>
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: '#22c55e',
                  marginBottom: '8px',
                  textShadow: '0 2px 8px rgba(34, 197, 94, 0.5)'
                }}>
                  {environmentalMetrics.renewableEnergy?.percentage}%
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Renewable Energy
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: '#60a5fa',
                  marginBottom: '8px',
                  textShadow: '0 2px 8px rgba(96, 165, 250, 0.5)'
                }}>
                  {environmentalMetrics.wasteManagement?.recyclingRate}%
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Recycling Rate
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '800',
                marginBottom: '12px'
              }}>
                Water Usage Efficiency
              </h4>
              <div style={{
                height: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: `${environmentalMetrics.waterUsage?.efficiency}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)',
                  borderRadius: '6px',
                  boxShadow: '0 0 10px rgba(96, 165, 250, 0.5)'
                }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{
                  color: '#cbd5e1',
                  fontSize: '13px',
                  fontWeight: '700'
                }}>
                  Total: {environmentalMetrics.waterUsage?.total?.toLocaleString()} m³
                </span>
                <span style={{
                  color: '#cbd5e1',
                  fontSize: '13px',
                  fontWeight: '700'
                }}>
                  Recycled: {environmentalMetrics.waterUsage?.recycled?.toLocaleString()} m³
                </span>
              </div>
            </div>

            <div>
              <h4 style={{
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '800',
                marginBottom: '10px'
              }}>
                Renewable Energy Sources
              </h4>
              {environmentalMetrics.renewableEnergy?.sources?.map((source, index) => (
                <div key={index} style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  paddingLeft: '10px'
                }}>
                  • {source}
                </div>
              ))}
              <div style={{
                marginTop: '12px',
                color: '#cbd5e1',
                fontSize: '13px',
                fontWeight: '700'
              }}>
                Target: {environmentalMetrics.renewableEnergy?.target}% by {environmentalMetrics.renewableEnergy?.timeline}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '25px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
        }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: '900',
            marginBottom: '20px',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '24px' }}>📊</span>
            Carbon Footprint Breakdown
          </h3>
          
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#fbbf24',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(251, 191, 36, 0.5)'
            }}>
              {carbonFootprint.total.toLocaleString()}
            </div>
            <div style={{
              color: '#94a3b8',
              fontSize: '13px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Total tCO2e
            </div>
          </div>
          
          {[
            { label: 'Scope 1 (Direct)', value: carbonFootprint.breakdown.scope1.value, percentage: carbonFootprint.breakdown.scope1.percentage, color: '#ef4444' },
            { label: 'Scope 2 (Electricity)', value: carbonFootprint.breakdown.scope2.value, percentage: carbonFootprint.breakdown.scope2.percentage, color: '#fbbf24' },
            { label: 'Scope 3 (Value Chain)', value: carbonFootprint.breakdown.scope3.value, percentage: carbonFootprint.breakdown.scope3.percentage, color: '#60a5fa' }
          ].map((scope, idx) => (
            <div key={idx} style={{ marginBottom: '18px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <span style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '800'
                }}>
                  {scope.label}
                </span>
                <span style={{
                  color: scope.color,
                  fontSize: '14px',
                  fontWeight: '800'
                }}>
                  {scope.value.toLocaleString()} tCO2e
                </span>
              </div>
              <div style={{
                height: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '5px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${scope.percentage}%`,
                  height: '100%',
                  background: scope.color,
                  borderRadius: '5px',
                  boxShadow: `0 0 10px ${scope.color}80`
                }}></div>
              </div>
            </div>
          ))}
          
          <div style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            marginTop: '20px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              background: '#22c55e',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '800',
              boxShadow: '0 2px 10px rgba(34, 197, 94, 0.4)'
            }}>
              Target: {carbonFootprint.target.toLocaleString()} tCO2e
            </span>
            <span style={{
              background: '#60a5fa',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '800',
              boxShadow: '0 2px 10px rgba(96, 165, 250, 0.4)'
            }}>
              Progress: {carbonFootprint.progress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const SocialTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
      {/* Labor Standards & Human Rights */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
          padding: '20px 24px',
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '800',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            <span style={{ fontSize: '24px' }}>👥</span>
            Labor Standards & Human Rights
          </h3>
        </div>
        
        <div style={{ padding: '24px' }}>
          {/* Top Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(34, 197, 94, 0.2)' }}>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#22c55e', marginBottom: '8px', textShadow: '0 2px 6px rgba(34, 197, 94, 0.3)' }}>
                {socialMetrics.laborStandards?.complianceRate}%
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Compliance Rate
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(59, 130, 246, 0.2)' }}>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#3b82f6', marginBottom: '8px', textShadow: '0 2px 6px rgba(59, 130, 246, 0.3)' }}>
                {socialMetrics.humanRights?.trainingCompletionRate}%
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Training Completion
              </div>
            </div>
          </div>

          {/* Supplier Audits */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>
              Supplier Audits
            </h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: '600' }}>Audited Suppliers</span>
              <span style={{ color: '#111827', fontWeight: '800' }}>
                {socialMetrics.laborStandards?.auditedSuppliers}/{socialMetrics.laborStandards?.auditedSuppliers}
              </span>
            </div>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: '100%', height: '100%', background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)', borderRadius: '4px', boxShadow: '0 0 10px rgba(34, 197, 94, 0.4)' }}></div>
            </div>
          </div>

          {/* Violations & Corrective Actions */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>
              Violations & Corrective Actions
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ textAlign: 'center', padding: '16px', border: '2px solid #fbbf24', borderRadius: '12px', background: 'rgba(251, 191, 36, 0.05)' }}>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#fbbf24', marginBottom: '4px' }}>
                  {socialMetrics.laborStandards?.violationsDetected}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '700' }}>Violations</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', border: '2px solid #22c55e', borderRadius: '12px', background: 'rgba(34, 197, 94, 0.05)' }}>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#22c55e', marginBottom: '4px' }}>
                  {socialMetrics.laborStandards?.correctiveActions}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '700' }}>Actions Taken</div>
              </div>
            </div>
          </div>

          {/* Human Rights Risk Assessment */}
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>
              Human Rights Risk Assessment
            </h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(251, 191, 36, 0.05)', borderRadius: '8px', marginBottom: '8px', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
              <span style={{ fontSize: '14px', color: '#4b5563', fontWeight: '600' }}>High-Risk Suppliers Identified</span>
              <span style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '800', boxShadow: '0 2px 6px rgba(251, 191, 36, 0.3)' }}>
                {socialMetrics.humanRights?.riskySuppliersIdentified}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(34, 197, 94, 0.05)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
              <span style={{ fontSize: '14px', color: '#4b5563', fontWeight: '600' }}>Mitigation Plans Active</span>
              <span style={{ background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '800', boxShadow: '0 2px 6px rgba(34, 197, 94, 0.3)' }}>
                {socialMetrics.humanRights?.mitigationPlansActive}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Community Impact & Diversity */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          padding: '20px 24px',
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '800',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            <HandshakeIcon size={24} />
            Community Impact & Diversity
          </h3>
        </div>
        
        <div style={{ padding: '24px' }}>
          {/* Top Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(16, 185, 129, 0.2)' }}>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#10b981', marginBottom: '8px', textShadow: '0 2px 6px rgba(16, 185, 129, 0.3)' }}>
                {socialMetrics.communityImpact?.localSourcingPercentage}%
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Local Sourcing
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(192, 132, 252, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(168, 85, 247, 0.2)' }}>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#a855f7', marginBottom: '8px', textShadow: '0 2px 6px rgba(168, 85, 247, 0.3)' }}>
                ${socialMetrics.communityImpact?.communityInvestment}M
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Community Investment
              </div>
            </div>
          </div>

          {/* Supplier Diversity */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '800', color: '#111827', marginBottom: '16px' }}>
              Supplier Diversity
            </h4>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280', fontWeight: '600' }}>Women-Owned</span>
                <span style={{ color: '#22c55e', fontWeight: '800' }}>{socialMetrics.diversityInclusion?.womenOwnedSuppliers}%</span>
              </div>
              <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${socialMetrics.diversityInclusion?.womenOwnedSuppliers}%`, height: '100%', background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)', borderRadius: '3px', boxShadow: '0 0 8px rgba(34, 197, 94, 0.4)' }}></div>
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280', fontWeight: '600' }}>Minority-Owned</span>
                <span style={{ color: '#3b82f6', fontWeight: '800' }}>{socialMetrics.diversityInclusion?.minorityOwnedSuppliers}%</span>
              </div>
              <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${socialMetrics.diversityInclusion?.minorityOwnedSuppliers}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)', borderRadius: '3px', boxShadow: '0 0 8px rgba(59, 130, 246, 0.4)' }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280', fontWeight: '600' }}>Small Business</span>
                <span style={{ color: '#fbbf24', fontWeight: '800' }}>{socialMetrics.diversityInclusion?.smallBusinessSuppliers}%</span>
              </div>
              <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${socialMetrics.diversityInclusion?.smallBusinessSuppliers}%`, height: '100%', background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)', borderRadius: '3px', boxShadow: '0 0 8px rgba(251, 191, 36, 0.4)' }}></div>
              </div>
            </div>
          </div>

          {/* Community Programs */}
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>
              Community Programs
            </h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', marginBottom: '8px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <span style={{ fontSize: '14px', color: '#4b5563', fontWeight: '600' }}>Jobs Created</span>
              <span style={{ fontSize: '16px', color: '#3b82f6', fontWeight: '900' }}>
                {socialMetrics.communityImpact?.jobsCreated?.toLocaleString()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <span style={{ fontSize: '14px', color: '#4b5563', fontWeight: '600' }}>Education Programs</span>
              <span style={{ fontSize: '16px', color: '#10b981', fontWeight: '900' }}>
                {socialMetrics.communityImpact?.educationPrograms}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const GovernanceTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '24px' }}>
      {/* Transparency & Reporting */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        height: 'fit-content'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          padding: '20px 24px',
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '800',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            <span style={{ fontSize: '22px' }}>👁️</span>
            Transparency & Reporting
          </h3>
        </div>
        
        <div style={{ padding: '24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px', padding: '20px', background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(251, 191, 36, 0.2)' }}>
            <div style={{ fontSize: '42px', fontWeight: '900', color: '#fbbf24', marginBottom: '8px', textShadow: '0 2px 6px rgba(251, 191, 36, 0.3)' }}>
              {governanceMetrics.transparency?.publicDisclosureScore}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Disclosure Score
            </div>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>
              Reporting Standards
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {governanceMetrics.transparency?.reportingStandards?.map((standard, index) => (
                <span key={index} style={{
                  background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 6px rgba(107, 114, 128, 0.3)'
                }}>
                  {standard}
                </span>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: '600' }}>Stakeholder Engagement</span>
              <span style={{ color: '#fbbf24', fontWeight: '800' }}>{governanceMetrics.transparency?.stakeholderEngagement}%</span>
            </div>
            <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${governanceMetrics.transparency?.stakeholderEngagement}%`, height: '100%', background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)', borderRadius: '3px', boxShadow: '0 0 8px rgba(251, 191, 36, 0.4)' }}></div>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: '600' }}>Board Independence</span>
              <span style={{ color: '#3b82f6', fontWeight: '800' }}>{governanceMetrics.transparency?.boardIndependence}%</span>
            </div>
            <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${governanceMetrics.transparency?.boardIndependence}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)', borderRadius: '3px', boxShadow: '0 0 8px rgba(59, 130, 246, 0.4)' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Ethics & Compliance */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        height: 'fit-content'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          padding: '20px 24px',
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '800',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            <span style={{ fontSize: '22px' }}>⚖️</span>
            Ethics & Compliance
          </h3>
        </div>
        
        <div style={{ padding: '24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px', padding: '20px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(16, 185, 129, 0.2)' }}>
            <div style={{ fontSize: '42px', fontWeight: '900', color: '#10b981', marginBottom: '8px', textShadow: '0 2px 6px rgba(16, 185, 129, 0.3)' }}>
              {governanceMetrics.ethicsCompliance?.codeOfConductSignatureRate}%
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Code Compliance
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: '600' }}>Ethics Training</span>
              <span style={{ color: '#10b981', fontWeight: '800' }}>{governanceMetrics.ethicsCompliance?.ethicsTrainingCompletion}%</span>
            </div>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${governanceMetrics.ethicsCompliance?.ethicsTrainingCompletion}%`, height: '100%', background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)', borderRadius: '4px', boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)' }}></div>
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>
              Whistleblower Program
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ textAlign: 'center', padding: '16px', border: '2px solid #3b82f6', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.05)' }}>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#3b82f6', marginBottom: '4px' }}>
                  {governanceMetrics.ethicsCompliance?.whistleblowerReports}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '700' }}>Reports</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', border: '2px solid #10b981', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.05)' }}>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#10b981', marginBottom: '4px' }}>
                  {governanceMetrics.ethicsCompliance?.investigationsCompleted}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '700' }}>Resolved</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Privacy & Security */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        height: 'fit-content'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
          padding: '20px 24px',
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '800',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            <span style={{ fontSize: '22px' }}>🛡️</span>
            Data Privacy & Security
          </h3>
        </div>
        
        <div style={{ padding: '24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px', padding: '20px', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.1) 100%)', borderRadius: '12px', border: '2px solid rgba(59, 130, 246, 0.2)' }}>
            <div style={{ fontSize: '42px', fontWeight: '900', color: '#3b82f6', marginBottom: '8px', textShadow: '0 2px 6px rgba(59, 130, 246, 0.3)' }}>
              {governanceMetrics.dataPrivacy?.gdprCompliance}%
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              GDPR Compliance
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: '600' }}>Privacy Training</span>
              <span style={{ color: '#3b82f6', fontWeight: '800' }}>{governanceMetrics.dataPrivacy?.privacyTrainingCompletion}%</span>
            </div>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${governanceMetrics.dataPrivacy?.privacyTrainingCompletion}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)', borderRadius: '4px', boxShadow: '0 0 10px rgba(59, 130, 246, 0.4)' }}></div>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#6b7280', fontWeight: '600' }}>Consent Management</span>
              <span style={{ color: '#10b981', fontWeight: '800' }}>{governanceMetrics.dataPrivacy?.consentManagement}%</span>
            </div>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${governanceMetrics.dataPrivacy?.consentManagement}%`, height: '100%', background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)', borderRadius: '4px', boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)' }}></div>
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <span style={{
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '800',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              display: 'inline-block'
            }}>
              {governanceMetrics.dataPrivacy?.dataBreachIncidents} Data Breaches (YTD)
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      padding: '0',
      margin: '0'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: 'clamp(30px, 5vw, 60px) clamp(20px, 4vw, 40px)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* SVG Pattern Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'clamp(15px, 3vw, 20px)' }}>
            <div style={{ flex: '1', minWidth: '280px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'clamp(50px, 8vw, 70px)',
                height: 'clamp(50px, 8vw, 70px)',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                marginBottom: 'clamp(10px, 2vw, 20px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
              }}>
                <span style={{ fontSize: 'clamp(24px, 4vw, 36px)' }}>♻️</span>
              </div>
              <h1 style={{
                fontSize: 'clamp(24px, 5vw, 42px)',
                fontWeight: '900',
                color: '#ffffff',
                marginBottom: 'clamp(8px, 1.5vw, 12px)',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                lineHeight: '1.2'
              }}>
                Supply Chain Ethics Overview
              </h1>
              <p style={{
                fontSize: 'clamp(14px, 2vw, 18px)',
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: '600',
                margin: 0,
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
              }}>
                ESG Compliance & Sustainable Supply Chain Management
              </p>
            </div>
            
            <button
              onClick={handleRefreshData}
              disabled={loading}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                padding: '14px 28px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                opacity: loading ? 0.6 : 1
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
              }}
            >
              <span style={{ fontSize: '18px' }}>🔄</span>
              Refresh Data
            </button>
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'clamp(12px, 2vw, 20px)', marginTop: 'clamp(20px, 4vw, 40px)' }}>
            <div style={{
              padding: 'clamp(16px, 3vw, 24px)',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '16px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}>
              <div style={{ fontSize: 'clamp(11px, 1.5vw, 14px)', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', marginBottom: 'clamp(6px, 1.2vw, 10px)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Overall ESG Grade
              </div>
              <div style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '900', color: 'white', textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)', lineHeight: '1' }}>
                {getESGGrade()?.grade || 'N/A'}
              </div>
            </div>

            <div style={{
              padding: 'clamp(16px, 3vw, 24px)',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '16px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}>
              <div style={{ fontSize: 'clamp(11px, 1.5vw, 14px)', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', marginBottom: 'clamp(6px, 1.2vw, 10px)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Total Suppliers
              </div>
              <div style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '900', color: 'white', textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)', lineHeight: '1' }}>
                {esgOverview?.totalSuppliers || 0}
              </div>
            </div>

            <div style={{
              padding: 'clamp(16px, 3vw, 24px)',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '16px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}>
              <div style={{ fontSize: 'clamp(11px, 1.5vw, 14px)', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', marginBottom: 'clamp(6px, 1.2vw, 10px)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Compliant Suppliers
              </div>
              <div style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '900', color: 'white', textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)', lineHeight: '1' }}>
                {esgOverview?.compliantSuppliers || 0}
              </div>
            </div>

            <div style={{
              padding: 'clamp(16px, 3vw, 24px)',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '16px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}>
              <div style={{ fontSize: 'clamp(11px, 1.5vw, 14px)', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', marginBottom: 'clamp(6px, 1.2vw, 10px)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Carbon Footprint
              </div>
              <div style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '900', color: 'white', textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)', lineHeight: '1' }}>
                {calculateCarbonFootprint()?.total || 0}
              </div>
              <div style={{ fontSize: 'clamp(11px, 1.5vw, 13px)', color: 'rgba(255, 255, 255, 0.85)', fontWeight: '600', marginTop: '5px' }}>
                tCO₂e
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.3)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(15px, 2vw, 20px) clamp(20px, 4vw, 40px)' }}>
          <div style={{ display: 'flex', gap: 'clamp(8px, 1.5vw, 10px)', flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { id: 'overview', label: 'ESG Overview', icon: '📊' },
              { id: 'environmental', label: 'Environmental', icon: '🌿' },
              { id: 'social', label: 'Social', icon: '👥' },
              { id: 'governance', label: 'Governance', icon: '⚖️' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  background: activeTab === tab.id 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: activeTab === tab.id 
                    ? '2px solid rgba(168, 85, 247, 0.5)'
                    : '2px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: 'clamp(10px, 1.5vw, 12px) clamp(16px, 3vw, 24px)',
                  color: '#ffffff',
                  fontSize: 'clamp(13px, 1.8vw, 15px)',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: activeTab === tab.id 
                    ? '0 4px 15px rgba(124, 58, 237, 0.4)'
                    : '0 2px 8px rgba(0, 0, 0, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: '1 1 auto',
                  minWidth: 'fit-content',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span style={{ fontSize: '18px' }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(20px, 4vw, 40px)' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'environmental' && <EnvironmentalTab />}
        {activeTab === 'social' && <SocialTab />}
        {activeTab === 'governance' && <GovernanceTab />}
      </div>
    </div>
  );
};

export default SupplyChainEthicsOverview;